import { QMulti } from '@educraft/base';
import { IExercise, ExerciseData, addResult } from '../exercise';
import { PdfUnit, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';
import { makeCombinedMulti, generateUnique } from './exeUtil';

export class ExerciseAbcBase implements IExercise {
  readonly fixedHeight = 0;
  readonly noWindow = false;
  readonly injectEq = false;
  readonly injectAbc = true;

  private rowHeight = PdfUnit.FromMillimeters(8);
  private margin = PdfUnit.FromMillimeters(1);
  private squareSize = PdfUnit.FromMillimeters(6);
  private marginColumn = PdfUnit.FromMillimeters(5);

  constructor(
    public readonly name: string,
    private columns: number
  ) {}

  draw(data: ExerciseData): void {
    const selAbc = data.item.selAbc.length > 0 ? data.item.selAbc : data.item.selEq;
    if (selAbc.length === 0) return;

    const gen = makeCombinedMulti(data);

    // Pre-generate enough items for all columns
    const count = Math.floor(data.rect.height / this.rowHeight / 2 + 2) * this.columns;
    const array = generateUnique(
      gen,
      data.data,
      count,
      (q) => `${q.texts.join(' ')}|${q.answers[q.answerIndex]}`
    );

    let itemIndex = 0;
    const colWidth = (data.rect.width + this.marginColumn) / this.columns;

    for (let c = 0; c < this.columns; c++) {
      let top = 0;
      const colRect = new PdfRect(
        data.rect.x + c * colWidth,
        data.rect.y,
        colWidth - this.marginColumn,
        data.rect.height
      );

      while (itemIndex < array.length) {
        const item = array[itemIndex];
        const height = this.rowHeight * (item.texts.length + 1);

        if (top + height > colRect.height) break;

        // Record result
        const answer = item.answers[item.answerIndex];
        if (answer.length <= 5) {
          addResult(
            data,
            item.texts.join(' '),
            `${String.fromCharCode(item.answerIndex + 65)} (${answer})`
          );
        } else {
          addResult(data, item.texts.join(' '), String.fromCharCode(item.answerIndex + 65));
        }

        // Draw question text lines
        for (let i = 0; i < item.texts.length; i++) {
          const r = new PdfRect(
            colRect.x,
            colRect.y + top + i * this.rowHeight,
            colRect.width,
            this.rowHeight
          );
          data.page.drawString(
            item.texts[i],
            GUtil.exerciseFont,
            PdfBrush.Black,
            null,
            r,
            ETextAlign.CenterLeft
          );
        }

        // Draw answer options in a row
        const answerWidth = colRect.width / item.answers.length;
        for (let i = 0; i < item.answers.length; i++) {
          const r = new PdfRect(
            colRect.x + i * answerWidth,
            colRect.y + top + item.texts.length * this.rowHeight,
            answerWidth,
            this.rowHeight
          );

          const r1 = new PdfRect(
            r.x,
            r.y + (this.rowHeight - this.squareSize) / 2,
            this.squareSize,
            this.squareSize
          );
          data.colorSchema.drawResult(data.page, r1);
          data.page.drawRectangle(GUtil.basePen, null, r1);

          const r2 = new PdfRect(
            r1.right + this.margin,
            r.y,
            r.width - this.squareSize - this.margin,
            r.height
          );
          data.page.drawString(
            item.answers[i],
            GUtil.exerciseFont,
            PdfBrush.Black,
            null,
            r2,
            ETextAlign.CenterLeft
          );
        }

        top += (item.texts.length + 1) * this.rowHeight;
        itemIndex++;
      }
    }
  }
}
